import { AnalysisStoreProvider } from '@modules/analysis/store/AnalysisStoreProvider';
import { FeedbackCountCitationContainer } from '@modules/common/components/FeedbackCountCitationContainer';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { LAST_5_YEARS } from '@modules/common/constants';
import { FeedbackListStoreProvider } from '@modules/feedback/store/useFeedbackListStore';
import { ArtefactCitationFeedbackDrawer } from '@modules/oracle/components/ArtefactCitationFeedbackDrawer';
import { UserQuotes } from '@modules/oracle/components/UserQuotes';
import {
  UserQuoteTokensByResponseID,
  UserQuotesByExampleID,
} from '@modules/oracle/types';
import {
  getDurationFromAssistantResponseEqlQuery,
  getWhereClauseFromAssistantResponseEqlQuery,
  trackWisdomEvents,
} from '@modules/oracle/utils';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { Box, Popper, Tooltip } from '@mui/material';
import {
  AnalysisUseCase,
  AnalysisViewFragment,
  AssistantResponseArtefactToken,
  FeedbackSummaryExample,
  Label,
  LabelType,
  TimeUnit,
  useGetAnalysisByTokenLazyQuery,
} from 'generated/graphql';
import { useMemo, useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { ArtefactCitationFeedbackList } from '../ArtefactCitationFeedbackList';
import { ArtefactCitationFeedbackListPopover } from '../ArtefactCitationFeedbackListPopover';
import { OracleQuantifyProvider } from '../OracleQuantifyProvider';
import { QuantifyCitationDialog } from '../QuantifyCitationDialog';

interface InsightProofIconButtonProps {
  children: React.ReactNode;
  feedbackSummaryExample: FeedbackSummaryExample | undefined;
  userQuoteTokensByResponseID: UserQuoteTokensByResponseID;
  userQuotesByExampleID: UserQuotesByExampleID | undefined;
  sessionID: string;
  label: Label | undefined;
  onClick?: (isOpen: boolean) => void;
  artefact?: Pick<
    AssistantResponseArtefactToken,
    'analysis' | 'feedbackCount' | 'forClaim'
  >;
  openCitedArtefactsInNewTab?: boolean;
  isFeedbackSidebarView?: boolean;
}

export const InsightProofIconButton = ({
  children,
  feedbackSummaryExample,
  userQuoteTokensByResponseID,
  userQuotesByExampleID,
  sessionID,
  label,
  onClick,
  artefact,
  openCitedArtefactsInNewTab,
  isFeedbackSidebarView,
}: InsightProofIconButtonProps) => {
  const {
    id: userID,
    tenant: { id: tenantID, timezone },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [shouldShowFeedbackDrawer, setShouldShowFeedbackDrawer] =
    useState(false);
  const [
    shouldShowQuantifyCitationDialog,
    setShouldShowQuantifyCitationDialog,
  ] = useState(false);

  const [
    shouldShowExpandedFeedbackDrawer,
    setShouldShowExpandedFeedbackDrawer,
  ] = useState(false);

  const [shouldShowCitationFeedbackList, setShouldShowCitationFeedbackList] =
    useState(false);

  const isOpen = Boolean(anchorEl);
  const isArtefactProof = !!artefact;

  const { viewToken, useCase, title } = artefact?.analysis ?? {};

  const [getAnalysisByToken] = useGetAnalysisByTokenLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const [fetchedAnalysis, setFetchedAnalysis] = useState<
    AnalysisViewFragment | undefined
  >();

  const isSingleRecordCollection =
    useCase === AnalysisUseCase.Collection && artefact?.feedbackCount === 1;

  const fetchAndSetAnalysis = async () => {
    if (!viewToken) {
      return;
    }

    try {
      const resp = await getAnalysisByToken({
        variables: {
          token: viewToken,
          userID,
          tenantID,
        },
      });

      setFetchedAnalysis(resp?.data?.getAnalysisByToken);
      return resp?.data?.getAnalysisByToken;
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to fetch the artifact.',
        props: {},
      });
    }
  };

  const onOpenArtefact = async (event: React.MouseEvent<HTMLElement>) => {
    if (
      useCase === AnalysisUseCase.Compare ||
      useCase === AnalysisUseCase.Default
    ) {
      fetchAndSetAnalysis();
      setShouldShowQuantifyCitationDialog(true);
      return;
    }

    if (openCitedArtefactsInNewTab) {
      fetchAndSetAnalysis();
      setAnchorEl(event.currentTarget);
      setShouldShowCitationFeedbackList(true);
      return;
    }

    fetchAndSetAnalysis();
    setShouldShowFeedbackDrawer(true);
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isSingleRecordCollection) {
      setShouldShowExpandedFeedbackDrawer(true);
      return;
    }

    if (artefact) {
      onOpenArtefact(event);
      trackWisdomEvents({
        eventName: 'Wisdom - Opened Artefact View from Citation',
        artefact: {
          title,
          useCase,
        },
        isFeedbackSidebarView,
      });
      return;
    }

    // TODO: remove all this post artefact citation goes live

    setAnchorEl(isOpen ? null : event.currentTarget);
    onClick?.(isOpen);

    trackWisdomEvents({
      eventName: !!label
        ? 'Wisdom - View Feedback For Label Clicked'
        : 'Wisdom - View Quotes From Users Clicked',
      label: label?.displayName,
    });
  };

  const onClickAway = () => {
    if (!anchorEl || artefact) {
      return;
    }
    setAnchorEl(null);
  };

  const shouldDisableButton = isArtefactProof
    ? false
    : !userQuotesByExampleID || !feedbackSummaryExample;

  const closeFeedbackList = () => {
    setShouldShowFeedbackDrawer(false);
  };

  const isLabelType = !!label;

  const getTooltipTitle = () => {
    if (shouldDisableButton || isOpen) {
      return null;
    }

    if (isSingleRecordCollection) {
      return 'View feedback record';
    }

    if (useCase === AnalysisUseCase.Collection) {
      return 'View Examples';
    }

    if (isLabelType) {
      return `View feedback for this ${
        label.type === LabelType.Aspect ? 'Tracked Keyword' : 'Reason'
      }`;
    }

    const getCitationType = () => {
      if (
        useCase === AnalysisUseCase.Default ||
        useCase === AnalysisUseCase.Compare
      ) {
        return 'insights';
      }
      if (artefact) {
        return 'feedback';
      }
      return 'quotes';
    };

    return `View ${getCitationType()} from users`;
  };

  const getCitationVariant = () => {
    if (isSingleRecordCollection) {
      return 'feedback';
    }

    if (
      useCase === AnalysisUseCase.Default ||
      useCase === AnalysisUseCase.Compare
    ) {
      return 'insights';
    }

    if (isArtefactProof) {
      return 'chatBubble';
    }

    return 'circular';
  };

  const queryContainer = useMemo(() => {
    const eqlQuery = fetchedAnalysis?.context.eql;
    return {
      AggregatePeriod: TimeUnit.Week,
      DateRange:
        eqlQuery && fetchedAnalysis?.useCase !== AnalysisUseCase.Collection
          ? (getDurationFromAssistantResponseEqlQuery(
              eqlQuery.properties.duration,
            ) as TimeRange)
          : (LAST_5_YEARS as TimeRange),
      WhereClause:
        getWhereClauseFromAssistantResponseEqlQuery({
          sourceDetailsMap: sourceDetailsMap,
          timezone,
          whereClause: fetchedAnalysis?.context.eqlExpression,
        }) ?? EmptyWhereClause,
    };
  }, [
    fetchedAnalysis?.context.eql,
    fetchedAnalysis?.context.eqlExpression,
    fetchedAnalysis?.useCase,
    sourceDetailsMap,
    timezone,
  ]);

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <Box
          component="span"
          sx={{ display: 'inline-block' }}
          aria-owns={
            shouldShowCitationFeedbackList
              ? 'citation-feedback-list-popover'
              : undefined
          }
          aria-haspopup="true"
        >
          <Tooltip
            arrow
            title={getTooltipTitle()}
            placement="top"
            disableInteractive
          >
            <Box>
              <FeedbackCountCitationContainer
                isDisabled={shouldDisableButton}
                isOpen={isOpen}
                variant={getCitationVariant()}
                onClick={handleClick}
                isLabelType={isLabelType}
              >
                {children}
              </FeedbackCountCitationContainer>
            </Box>
          </Tooltip>
          {!shouldDisableButton &&
          userQuotesByExampleID &&
          feedbackSummaryExample ? (
            <Popper
              placement="bottom-end"
              open={isOpen}
              anchorEl={anchorEl}
              className="user-quotes-popper"
              sx={{
                width: 'max-content',
                zIndex: (theme) => theme.zIndex.drawer,
              }}
            >
              <UserQuotes
                sessionID={sessionID}
                labelID={label?.id}
                userQuotesByExampleID={userQuotesByExampleID}
                feedbackSummaryExample={feedbackSummaryExample}
                userQuoteTokensByResponseID={userQuoteTokensByResponseID}
              />
            </Popper>
          ) : null}
        </Box>
      </ClickAwayListener>
      <FeedbackListStoreProvider>
        {shouldShowFeedbackDrawer && (
          <AnalysisStoreProvider useCase={useCase ?? AnalysisUseCase.Default}>
            <ArtefactCitationFeedbackList
              shouldShowFeedbackDrawer={shouldShowFeedbackDrawer}
              closeFeedbackDrawer={closeFeedbackList}
              analysis={fetchedAnalysis}
              quoteContextClaim={artefact?.forClaim}
              queryContainer={queryContainer}
            />
          </AnalysisStoreProvider>
        )}
      </FeedbackListStoreProvider>
      {shouldShowQuantifyCitationDialog && (
        <OracleQuantifyProvider>
          <QuantifyCitationDialog
            isOpen={shouldShowQuantifyCitationDialog}
            onClose={() => {
              setShouldShowQuantifyCitationDialog(false);
            }}
            isReadOnly
            analysis={fetchedAnalysis}
          />
        </OracleQuantifyProvider>
      )}
      {shouldShowCitationFeedbackList && (
        <ArtefactCitationFeedbackListPopover
          isOpen={shouldShowCitationFeedbackList}
          onClose={() => {
            setShouldShowCitationFeedbackList(false);
            setAnchorEl(null);
          }}
          analysis={fetchedAnalysis}
          quoteContextClaim={artefact?.forClaim}
          anchorEl={anchorEl}
        />
      )}
      {shouldShowExpandedFeedbackDrawer && (
        <ArtefactCitationFeedbackDrawer
          isOpen={shouldShowExpandedFeedbackDrawer}
          onClose={() => {
            setShouldShowExpandedFeedbackDrawer(false);
          }}
          artefact={artefact}
        />
      )}
    </>
  );
};
